// client/src/pages/PTO/FlexFridaysPanel.tsx
// Shows who is working on the next Friday in the selected week.

import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Star, ArrowLeftRight, Pencil, Trash2, Plus } from 'lucide-react';
import type { PTOEntry, PTOEmployee } from './ptoTypes';
import { formatDate } from './ptoUtils';
import { OFFICES } from './ptoConstants';

// ─── Props ──────────────────────────────────────────────────────────────────

interface FlexFridaysPanelProps {
  entries: PTOEntry[];
  employees: PTOEmployee[];
  currentWeek: Date; // Monday of the displayed week
  canEdit: boolean;
  onAddFridayWorker?: (fridayDate: string) => void;
  onEditEntry?: (entry: PTOEntry) => void;
  onDeleteEntry?: (id: string) => Promise<void>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function fridayIso(monday: Date): string {
  const fri = new Date(monday);
  fri.setDate(monday.getDate() + 4);
  const y = fri.getFullYear();
  const m = String(fri.getMonth() + 1).padStart(2, '0');
  const d = String(fri.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ─── Component ──────────────────────────────────────────────────────────────

const FlexFridaysPanel: React.FC<FlexFridaysPanelProps> = ({
  entries,
  employees,
  currentWeek,
  canEdit,
  onAddFridayWorker,
  onEditEntry,
  onDeleteEntry,
}) => {
  const fridayDate = useMemo(() => fridayIso(currentWeek), [currentWeek]);

  // Build employee lookup
  const empMap = useMemo(() => {
    const m = new Map<string, PTOEmployee>();
    for (const e of employees) m.set(e.staffIndex, e);
    return m;
  }, [employees]);

  // Filter Friday Work entries for this Friday, sort, then group by office.
  const fridayWorkers = useMemo(() => {
    const matched = entries
      .filter(
        (e) => e.entryDate === fridayDate && e.entryType === 'Friday Work',
      )
      .map((entry) => ({
        entry,
        employee: empMap.get(entry.staffIndex),
      }))
      .filter(
        (item): item is { entry: PTOEntry; employee: PTOEmployee } =>
          item.employee !== undefined,
      );

    // ATCs first, then by sortOrder
    matched.sort((a, b) => {
      if (a.employee.isAtc !== b.employee.isAtc) {
        return a.employee.isAtc ? -1 : 1;
      }
      return a.employee.sortOrder - b.employee.sortOrder;
    });

    // Group into offices, preserving OFFICES order (matches PTOHeatMap).
    // matched is already sorted, so filtering keeps each office's order.
    const groups: {
      office: string;
      color: string;
      items: { entry: PTOEntry; employee: PTOEmployee }[];
    }[] = [];
    for (const o of OFFICES) {
      const items = matched.filter((item) => item.employee.office === o.value);
      if (items.length > 0) {
        groups.push({ office: o.value, color: o.color, items });
      }
    }
    return groups;
  }, [entries, fridayDate, empMap]);

  const isEmpty = fridayWorkers.length === 0;

  return (
    <Wrap className="card">
      <Header className="hd">
        <HeaderLeft>
          <Title>Flex Friday</Title>
          <DateLabel>{formatDate(fridayDate)}</DateLabel>
        </HeaderLeft>
        {canEdit && onAddFridayWorker && (
          <AddBtn type="button" onClick={() => onAddFridayWorker(fridayDate)}>
            <Plus size={12} />
            Add
          </AddBtn>
        )}
      </Header>

      <Body className="bd">
        {isEmpty ? (
          <EmptyMsg>No scheduled Friday workers this week</EmptyMsg>
        ) : (
          fridayWorkers.map(({ office, color, items }) => (
            <OfficeGroup key={office}>
              <OfficeHeader>
                <OfficePill $color={color}>{office}</OfficePill>
                <OfficeCount>{items.length}</OfficeCount>
              </OfficeHeader>
              <CardGrid>
                {items.map(({ entry, employee }) => {
                  const partnerEmp = entry.swapPartner
                    ? empMap.get(entry.swapPartner)
                    : null;

                  return (
                    <WorkerCard key={entry.id} $isAtc={employee.isAtc}>
                      <CardTop>
                        {employee.isAtc && (
                          <AtcBadge>
                            <Star size={9} />
                            ATC
                          </AtcBadge>
                        )}
                        {canEdit && (
                          <CardActions>
                            {onEditEntry && (
                              <IconBtn
                                title="Edit"
                                onClick={() => onEditEntry(entry)}
                              >
                                <Pencil size={11} />
                              </IconBtn>
                            )}
                            {onDeleteEntry && (
                              <IconBtn
                                $danger
                                title="Delete"
                                onClick={async () => {
                                  if (window.confirm(`Remove ${employee.displayName} from Friday Work?`)) {
                                    await onDeleteEntry(entry.id);
                                  }
                                }}
                              >
                                <Trash2 size={11} />
                              </IconBtn>
                            )}
                          </CardActions>
                        )}
                      </CardTop>

                      <EmpName>{employee.displayName}</EmpName>

                      {partnerEmp && (
                        <SwapIndicator>
                          <ArrowLeftRight size={11} />
                          <span>Swap: {partnerEmp.displayName}</span>
                        </SwapIndicator>
                      )}
                    </WorkerCard>
                  );
                })}
              </CardGrid>
            </OfficeGroup>
          ))
        )}
      </Body>
    </Wrap>
  );
};

export default FlexFridaysPanel;

// ─── Styled Components ──────────────────────────────────────────────────────

const Wrap = styled.div``;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex: 1;
`;

const AddBtn = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid rgba(122, 162, 255, 0.4);
  background: rgba(122, 162, 255, 0.08);
  color: var(--accent);
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(122, 162, 255, 0.18);
  }
`;

const CardActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
`;

const IconBtn = styled.button<{ $danger?: boolean }>`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  color: var(--muted);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ $danger }) =>
      $danger ? 'rgba(255,107,107,.15)' : 'rgba(255,255,255,.08)'};
    color: ${({ $danger }) => ($danger ? '#ff6b6b' : 'var(--text)')};
  }
`;

const Title = styled.span`
  font-size: 14px;
  font-weight: 600;
`;

const DateLabel = styled.span`
  font-size: 12px;
  color: var(--muted);
`;

const Body = styled.div``;

const OfficeGroup = styled.div`
  & + & {
    margin-top: 8px;
  }
`;

const OfficeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
`;

const OfficeCount = styled.span`
  font-size: 11px;
  color: var(--muted);
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 6px;
`;

const WorkerCard = styled.div<{ $isAtc: boolean }>`
  padding: 6px 9px;
  border-radius: 6px;
  background: ${({ $isAtc }) =>
    $isAtc ? 'rgba(255,209,102,.06)' : 'rgba(255,255,255,.03)'};
  border: 1px solid
    ${({ $isAtc }) =>
      $isAtc ? 'rgba(255,209,102,.25)' : 'var(--border)'};
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AtcBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 9px;
  font-weight: 700;
  background: rgba(255, 209, 102, 0.2);
  color: #ffd166;
  border-radius: 4px;
  padding: 1px 5px;
`;

const OfficePill = styled.span<{ $color: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ $color }) => $color}33;
  color: ${({ $color }) => $color};
`;

const EmpName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--text);
`;

const SwapIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--muted);
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 24px 16px;
  color: var(--muted);
  font-size: 13px;
`;
